import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { TreePine, Users, Target, Heart, Mail, Globe, Award, Shield } from 'lucide-react';

const About = () => {
  const { language } = useLanguage();

  const content = {
    sv: {
      title: "Om Skogskalkyl",
      subtitle: "Verktyget för medvetna skogsägare",
      mission: {
        title: "Vår mission",
        text: "Skogskalkyl utvecklades för att hjälpa skogsägare att fatta välgrundade ekonomiska beslut. Vi tror att rätt information leder till bättre skogsskötsel – för både plånboken och miljön."
      },
      team: {
        title: "Teamet bakom",
        text: "Skogskalkyl drivs av Hodema, ett svenskt företag med passion för hållbart skogsbruk och digital innovation. Vi kombinerar skogsbruksexpertis med modern teknik för att göra komplexa beräkningar tillgängliga för alla."
      },
      values: {
        title: "Våra värderingar",
        items: [
          { icon: Target, title: "Precision", text: "Beräkningar baserade på officiell svensk skogsstatistik" },
          { icon: Shield, title: "Tillförlitlighet", text: "Data från SLU Riksskogstaxeringen och Skogsstyrelsen" },
          { icon: Heart, title: "Hållbarhet", text: "Främja ansvarsfull skogsskötsel för kommande generationer" },
          { icon: Award, title: "Tillgänglighet", text: "Enkla verktyg för alla – från nybörjare till proffs" }
        ]
      },
      contact: {
        title: "Kontakta oss",
        email: "info@hodema.se",
        text: "Har du frågor eller förslag? Vi älskar att höra från våra användare!"
      },
      languages: {
        title: "Språkstöd",
        text: "Skogskalkyl finns tillgänglig på svenska, tyska, engelska och samiska för att nå så många skogsägare som möjligt."
      }
    },
    de: {
      title: "Über Skogskalkyl",
      subtitle: "Das Werkzeug für bewusste Waldbesitzer",
      mission: {
        title: "Unsere Mission",
        text: "Skogskalkyl wurde entwickelt, um Waldbesitzern fundierte wirtschaftliche Entscheidungen zu ermöglichen. Wir glauben, dass die richtigen Informationen zu besserer Waldbewirtschaftung führen – für Ihr Budget und die Umwelt."
      },
      team: {
        title: "Das Team dahinter",
        text: "Skogskalkyl wird von Hodema betrieben, einem schwedischen Unternehmen mit Leidenschaft für nachhaltige Forstwirtschaft und digitale Innovation. Wir kombinieren Forstwirtschafts-Expertise mit moderner Technologie."
      },
      values: {
        title: "Unsere Werte",
        items: [
          { icon: Target, title: "Präzision", text: "Berechnungen basierend auf offizieller schwedischer Forststatistik" },
          { icon: Shield, title: "Zuverlässigkeit", text: "Daten von SLU Riksskogstaxeringen und Skogsstyrelsen" },
          { icon: Heart, title: "Nachhaltigkeit", text: "Förderung verantwortungsvoller Waldbewirtschaftung" },
          { icon: Award, title: "Zugänglichkeit", text: "Einfache Werkzeuge für alle – vom Anfänger bis zum Profi" }
        ]
      },
      contact: {
        title: "Kontaktieren Sie uns",
        email: "info@hodema.se",
        text: "Haben Sie Fragen oder Vorschläge? Wir freuen uns über Ihr Feedback!"
      },
      languages: {
        title: "Sprachunterstützung",
        text: "Skogskalkyl ist auf Schwedisch, Deutsch, Englisch und Samisch verfügbar."
      }
    },
    en: {
      title: "About Skogskalkyl",
      subtitle: "The tool for conscious forest owners",
      mission: {
        title: "Our Mission",
        text: "Skogskalkyl was developed to help forest owners make informed economic decisions. We believe that the right information leads to better forest management – for both your wallet and the environment."
      },
      team: {
        title: "The Team Behind",
        text: "Skogskalkyl is operated by Hodema, a Swedish company with a passion for sustainable forestry and digital innovation. We combine forestry expertise with modern technology."
      },
      values: {
        title: "Our Values",
        items: [
          { icon: Target, title: "Precision", text: "Calculations based on official Swedish forest statistics" },
          { icon: Shield, title: "Reliability", text: "Data from SLU Riksskogstaxeringen and Skogsstyrelsen" },
          { icon: Heart, title: "Sustainability", text: "Promoting responsible forest management for future generations" },
          { icon: Award, title: "Accessibility", text: "Simple tools for everyone – from beginners to professionals" }
        ]
      },
      contact: {
        title: "Contact Us",
        email: "info@hodema.se",
        text: "Have questions or suggestions? We love hearing from our users!"
      },
      languages: {
        title: "Language Support",
        text: "Skogskalkyl is available in Swedish, German, English, and Sami."
      }
    },
    se: {
      title: "Skogskalkyl birra",
      subtitle: "Reaidu dihtomielat vuovdeeaiggádiidda",
      mission: {
        title: "Min mihttomearri",
        text: "Skogskalkyl ráhkaduvvui veahkehit vuovdeeaiggádiid dahkat buriid ekonomalaš mearrádusaid. Mii jáhkkit ahte rievttes dieđut dagahit buoret vuovdedikšuma."
      },
      team: {
        title: "Joavku duogábealde",
        text: "Skogskalkyl lea Hodema doaibma, ruoŧŧelaš fitnodaga mas lea beroštupmi suvdilis vuovdedollui ja digitála innovašuvdnii."
      },
      values: {
        title: "Min árvvut",
        items: [
          { icon: Target, title: "Dárkkilvuohta", text: "Rehkenastimat vuođđuduvvon virggálaš statistihkkii" },
          { icon: Shield, title: "Luohttehahttivuohta", text: "Dieđut SLU Riksskogstaxeringen ja Skogsstyrelsen" },
          { icon: Heart, title: "Suvdilvuohta", text: "Ovddida ovddasvástideaddji vuovdedikšuma" },
          { icon: Award, title: "Olámuttisvuohta", text: "Álki reaiddut buohkaide" }
        ]
      },
      contact: {
        title: "Váldde oktavuođa",
        email: "info@hodema.se",
        text: "Leat go gažaldagat dahje evttohusat? Mii ráhkistit gullat min geavaheddjiin!"
      },
      languages: {
        title: "Gielladoarjja",
        text: "Skogskalkyl gávdno ruoŧŧagillii, duiskagillii, eaŋgalasgillii ja sámegillii."
      }
    }
  };

  const c = content[language] || content.sv;

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="about-page">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex justify-center">
          <div className="p-4 rounded-full bg-forest-100">
            <TreePine className="h-12 w-12 text-forest-700" />
          </div>
        </div>
        <h1 className="text-4xl font-bold text-forest-800">{c.title}</h1>
        <p className="text-xl text-muted-foreground">{c.subtitle}</p>
      </div>

      {/* Mission */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-forest-700">
            <Target className="h-5 w-5" />
            {c.mission.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground leading-relaxed">{c.mission.text}</p>
        </CardContent>
      </Card>

      {/* Team */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-forest-700">
            <Users className="h-5 w-5" />
            {c.team.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground leading-relaxed">{c.team.text}</p>
        </CardContent>
      </Card>

      {/* Values */}
      <Card>
        <CardHeader>
          <CardTitle className="text-forest-700">{c.values.title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {c.values.items.map((item, index) => (
              <div key={index} className="flex gap-4">
                <div className="p-2 rounded-lg bg-forest-50 h-fit">
                  <item.icon className="h-5 w-5 text-forest-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-forest-800">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Language Support */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-forest-700">
            <Globe className="h-5 w-5" />
            {c.languages.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground mb-4">{c.languages.text}</p>
          <div className="flex flex-wrap gap-2">
            <span className="px-3 py-1 bg-forest-50 rounded-full text-sm">🇸🇪 Svenska</span>
            <span className="px-3 py-1 bg-forest-50 rounded-full text-sm">🇩🇪 Deutsch</span>
            <span className="px-3 py-1 bg-forest-50 rounded-full text-sm">🇬🇧 English</span>
            <span className="px-3 py-1 bg-forest-50 rounded-full text-sm">🏳️ Sámegiella</span>
          </div>
        </CardContent>
      </Card>

      {/* Contact */}
      <Card className="bg-forest-50 border-forest-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-forest-700">
            <Mail className="h-5 w-5" />
            {c.contact.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground mb-4">{c.contact.text}</p>
          <a 
            href={`mailto:${c.contact.email}`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-forest-700 text-white rounded-lg hover:bg-forest-800 transition-colors"
            data-testid="contact-email-link"
          >
            <Mail className="h-4 w-4" />
            {c.contact.email}
          </a>
        </CardContent>
      </Card>
    </div>
  );
};

export default About;
